import { faCircleInfo, faXmark } from '@hg-akademie-ban/ban-elevate-assets/icons/regular';
import { Tooltip as TooltipPrimitive } from 'radix-ui';
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useState } from 'react';

import { cn } from '@/lib/utils.js';
import { Icon } from '@/components/ui/icon.js';

const tooltipContentStyles = cn([
  'z-50 w-fit max-w-xs rounded-xs p-12',
  'paragraph-xs',
  'bg-brand-knockout text-primary-knockout',
  'ring-1 ring-(--ld-color-background-brand-knockout)',
  'origin-(--radix-tooltip-content-transform-origin)',
  // Animation
  'animate-in fade-in-0',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
  // Slide direction
  'data-[side=bottom]:slide-in-from-top-8',
  'data-[side=left]:slide-in-from-right-8',
  'data-[side=right]:slide-in-from-left-8',
  'data-[side=top]:slide-in-from-bottom-8',
]);

export type TooltipProps = {
  /** Trigger element rendered with Radix asChild. */
  trigger: ReactElement;
  /** Tooltip body content. */
  children: ReactNode;
  /** When set, displays a titled header with an info icon. */
  headline?: string;
  /** Arrow position relative to the tooltip content. */
  arrowPosition?: 'bottom' | 'left' | 'right' | 'top';
  /** When provided, renders a dismiss button. The tooltip closes and calls this handler. */
  onDismiss?: () => void;
  /** Additional className for the tooltip content. */
  className?: string;
  /** Portal target container. */
  portalContainer?: HTMLElement | null;
  /** Controlled open state. */
  open?: boolean;
  /** Callback when open state changes. */
  onOpenChange?: (open: boolean) => void;
  /** Uncontrolled default open state. */
  defaultOpen?: boolean;
};

export function Tooltip({
  trigger,
  children,
  headline,
  arrowPosition = 'top',
  onDismiss,
  className,
  portalContainer,
  open: controlledOpen,
  onOpenChange,
  defaultOpen,
}: Readonly<TooltipProps>) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen ?? false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const handleOpenChange = useCallback(
    (value: boolean) => {
      if (!isControlled) setInternalOpen(value);
      onOpenChange?.(value);
    },
    [isControlled, onOpenChange],
  );

  function handleDismiss() {
    handleOpenChange(false);
    onDismiss?.();
  }

  return (
    <TooltipPrimitive.Provider delayDuration={0}>
      <TooltipPrimitive.Root data-slot="tooltip" open={open} onOpenChange={handleOpenChange}>
        <TooltipPrimitive.Trigger data-slot="tooltip-trigger" asChild>
          {trigger}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal container={portalContainer}>
          <TooltipPrimitive.Content
            data-slot="tooltip-content"
            sideOffset={0}
            side={arrowPosition}
            className={cn(tooltipContentStyles, className)}
          >
            {headline ? (
              <div className="flex items-start gap-8">
                <Icon icon={faCircleInfo} size="small" className="mt-2" />
                <div className="flex-1">
                  <p className="label-default mb-4">{headline}</p>
                  {children}
                </div>
                {onDismiss && (
                  <button
                    type="button"
                    aria-label="Dismiss tooltip"
                    onClick={handleDismiss}
                    className="shrink-0 cursor-pointer"
                  >
                    <Icon icon={faXmark} size="small" className="text-primary-knockout" />
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-start gap-8">
                <div className="flex-1">{children}</div>
                {onDismiss && (
                  <button
                    type="button"
                    aria-label="Dismiss tooltip"
                    onClick={handleDismiss}
                    className="shrink-0 cursor-pointer"
                  >
                    <Icon icon={faXmark} size="small" className="text-primary-knockout" />
                  </button>
                )}
              </div>
            )}
            <TooltipPrimitive.Arrow width={16} height={12} asChild>
              <svg
                className="fill-(--ld-color-background-brand-knockout)"
                viewBox="0 0 16 11.2"
                preserveAspectRatio="none"
              >
                <path d="M7.16795 10.752C7.56377 11.346 8.43623 11.346 8.83205 10.752L16 0L0 0Z" />
              </svg>
            </TooltipPrimitive.Arrow>
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
